// src/components/WhiteScreenDetector.tsx

import React, { useEffect, useRef } from 'react';
import { errorTracking } from '../utils/errorTracking';

interface WhiteScreenDetectorProps {
  children: React.ReactNode;
  timeout?: number; // 検出タイムアウト（ミリ秒）
  enabled?: boolean; // 検出を有効にするか
}

/**
 * 白い画面（レンダリング失敗）を検出するコンポーネント
 * 指定時間内に子コンポーネントがレンダリングされない場合、
 * 白い画面として検出してログを送信
 */
export const WhiteScreenDetector: React.FC<WhiteScreenDetectorProps> = ({
  children,
  timeout = 10000, // デフォルト10秒
  enabled = true
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasDetectedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const checkRendering = () => {
      if (hasDetectedRef.current) return;

      const container = containerRef.current;
      if (!container) {
        // コンテナ要素が見つからない場合は白い画面として検出
        errorTracking.logWhiteScreenDetection();
        errorTracking.logCustomError('Container element not found', {
          detection_type: 'missing_container',
          timeout_ms: timeout,
          user_agent: navigator.userAgent,
          url: window.location.href
        });
        hasDetectedRef.current = true;
        return;
      }

      // コンテナの子要素をチェック
      const hasContent = container.children.length > 0;
      const hasVisibleContent = Array.from(container.children).some(child => {
        const element = child as HTMLElement;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               style.opacity !== '0';
      });

      // コンテナのサイズをチェック
      const rect = container.getBoundingClientRect();
      const hasSize = rect.width > 0 && rect.height > 0;

      if (!hasContent || !hasVisibleContent || !hasSize) {
        // 白い画面として検出
        errorTracking.logWhiteScreenDetection();
        errorTracking.logCustomError('White screen detected', {
          detection_type: 'render_failure',
          has_content: hasContent,
          has_visible_content: hasVisibleContent,
          has_size: hasSize,
          container_width: rect.width,
          container_height: rect.height,
          children_count: container.children.length,
          timeout_ms: timeout,
          user_agent: navigator.userAgent,
          url: window.location.href,
          // iPhone特有の情報
          is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent),
          is_safari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
          device_pixel_ratio: window.devicePixelRatio,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          // メモリ情報（可能な場合）
          memory_info: (performance as any).memory ? {
            used: (performance as any).memory.usedJSHeapSize,
            total: (performance as any).memory.totalJSHeapSize,
            limit: (performance as any).memory.jsHeapSizeLimit
          } : null
        });
        hasDetectedRef.current = true;
      }
    };

    // 指定時間後にチェック実行
    timeoutRef.current = setTimeout(checkRendering, timeout);

    // クリーンアップ
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeout, enabled]);

  // 正常にレンダリングされた場合のマーカー
  useEffect(() => {
    if (enabled && containerRef.current) {
      // レンダリングが完了したらタイムアウトをクリア
      const timer = setTimeout(() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }, 100); // 短い遅延でレンダリング完了を確認

      return () => clearTimeout(timer);
    }
  }, [enabled, children]);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div 
      ref={containerRef}
      style={{ 
        width: '100%', 
        height: '100%',
        minHeight: '100vh' // 最小高さを確保
      }}
      data-white-screen-detector="true"
    >
      {children}
    </div>
  );
};

export default WhiteScreenDetector;
